import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { public: true },
  },
  {
    // Encuesta de satisfaccion que responde el cliente desde el correo (sin sesion).
    path: '/encuesta/:token',
    name: 'survey',
    component: () => import('@/pages/SurveyPage.vue'),
    meta: { public: true },
  },
  {
    // Portal de seguimiento del cliente (enlace sin sesion).
    path: '/seguimiento/:token',
    name: 'tracking',
    component: () => import('@/pages/TrackingPage.vue'),
    meta: { public: true },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/pages/ForbiddenPage.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
        meta: { breadcrumb: 'Panel' },
      },
      {
        path: 'cases',
        name: 'cases',
        component: () => import('@/pages/cases/CasesListPage.vue'),
        meta: { breadcrumb: 'Casos', permission: 'case:view' },
      },
      {
        path: 'cases/new',
        name: 'case-create',
        component: () => import('@/pages/cases/CaseCreatePage.vue'),
        meta: { breadcrumb: 'Nuevo caso', permission: 'case:create' },
      },
      {
        path: 'cases/:id',
        name: 'case-detail',
        component: () => import('@/pages/cases/CaseDetailPage.vue'),
        meta: { breadcrumb: 'Detalle del caso', permission: 'case:view' },
      },
      {
        path: 'customers',
        name: 'customers',
        component: () => import('@/pages/CustomersPage.vue'),
        meta: { breadcrumb: 'Clientes', permission: 'case:view' },
      },
      {
        path: 'tasks',
        name: 'tasks',
        component: () => import('@/pages/TasksPage.vue'),
        meta: { breadcrumb: 'Mis tareas' },
      },
      {
        path: 'approvals',
        name: 'approvals',
        component: () => import('@/pages/ApprovalsPage.vue'),
        meta: { breadcrumb: 'Aprobaciones', permission: 'case:approve' },
      },
      {
        path: 'knowledge-base',
        name: 'knowledge-base',
        component: () => import('@/pages/KnowledgeBasePage.vue'),
        meta: { breadcrumb: 'Base de conocimiento', permission: 'knowledge:view' },
      },
      {
        path: 'app',
        name: 'app-overview',
        component: () => import('@/pages/app/AppOverviewPage.vue'),
        meta: { breadcrumb: 'App Domika', permission: 'app:view' },
      },
      {
        path: 'app/technicians',
        name: 'app-technicians',
        component: () => import('@/pages/app/AppTechniciansPage.vue'),
        meta: { breadcrumb: 'Profesionales de la app', permission: 'app:view' },
      },
      {
        path: 'app/disputes',
        name: 'app-disputes',
        component: () => import('@/pages/app/AppDisputesPage.vue'),
        meta: { breadcrumb: 'Disputas', permission: 'app:view' },
      },
      {
        path: 'app/requests/:id',
        name: 'app-request',
        component: () => import('@/pages/app/AppRequestDetailPage.vue'),
        meta: { breadcrumb: 'Servicio de la app', permission: 'app:view_users' },
      },
      {
        path: 'app/users',
        name: 'app-users',
        component: () => import('@/pages/app/AppUsersPage.vue'),
        meta: { breadcrumb: 'Usuarios de la app', permission: 'app:view_users' },
      },
      {
        path: 'app/deletions',
        name: 'app-deletions',
        component: () => import('@/pages/app/AppDeletionsPage.vue'),
        meta: { breadcrumb: 'Eliminación de cuentas', permission: 'app:view_users' },
      },
      {
        path: 'app/deletions/:id',
        name: 'app-deletion',
        component: () => import('@/pages/app/AppDeletionDetailPage.vue'),
        meta: { breadcrumb: 'Solicitud de eliminación', permission: 'app:view_users' },
      },
      {
        path: 'app/penalties',
        name: 'app-penalties',
        component: () => import('@/pages/app/AppPenaltiesPage.vue'),
        meta: { breadcrumb: 'Penalizaciones', permission: 'app:view_users' },
      },
      {
        path: 'app/growth',
        name: 'app-growth',
        component: () => import('@/pages/app/AppGrowthPage.vue'),
        meta: { breadcrumb: 'Crecimiento', permission: 'app:view' },
      },
      {
        path: 'app/coupons',
        name: 'app-coupons',
        component: () => import('@/pages/app/AppCouponsPage.vue'),
        meta: { breadcrumb: 'Códigos promocionales', permission: 'app:view' },
      },
      {
        path: 'app/usage',
        name: 'app-usage',
        component: () => import('@/pages/app/FirebaseUsagePage.vue'),
        meta: { breadcrumb: 'Uso de Firebase', permission: 'infra:view' },
      },
      {
        path: 'app/business',
        name: 'app-business',
        component: () => import('@/pages/app/AppBusinessPage.vue'),
        meta: { breadcrumb: 'Métricas del negocio', permission: 'app:view' },
      },
      {
        path: 'app/play-testing',
        name: 'app-play-testing',
        component: () => import('@/pages/app/AppPlayTestingPage.vue'),
        meta: { breadcrumb: 'Prueba cerrada de Play', permission: 'playtest:view' },
      },
      {
        path: 'app/push',
        name: 'app-push',
        component: () => import('@/pages/app/AppPushPage.vue'),
        meta: { breadcrumb: 'Notificaciones a la app', permission: 'app:send_notifications' },
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@/pages/CalendarPage.vue'),
        meta: { breadcrumb: 'Calendario' },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/pages/ReportsPage.vue'),
        meta: { breadcrumb: 'Reportes', permission: 'report:view' },
      },
      {
        path: 'customers/:id',
        name: 'customer-detail',
        component: () => import('@/pages/CustomerDetailPage.vue'),
        meta: { breadcrumb: 'Ficha del cliente', permission: 'case:view' },
      },
      {
        path: 'templates',
        name: 'templates',
        component: () => import('@/pages/TemplatesPage.vue'),
        meta: { breadcrumb: 'Plantillas de respuesta', permission: 'template:manage' },
      },
      {
        path: 'design',
        name: 'design',
        component: () => import('@/pages/design/DesignPage.vue'),
        meta: { breadcrumb: 'Diseño', permission: 'design:request' },
      },
      {
        path: 'design/:id',
        name: 'design-detail',
        component: () => import('@/pages/design/DesignDetailPage.vue'),
        meta: { breadcrumb: 'Solicitud de diseño', permission: 'design:request' },
      },
      {
        path: 'finance',
        name: 'finance',
        component: () => import('@/pages/finance/FinancePage.vue'),
        meta: { breadcrumb: 'Finanzas', anyPermission: ['finance:view', 'finance:refunds'] },
      },
      {
        path: 'legal',
        name: 'legal',
        component: () => import('@/pages/legal/LegalPage.vue'),
        meta: { breadcrumb: 'Legal', permission: 'legal:manage' },
      },
      {
        path: 'marketing',
        name: 'marketing',
        component: () => import('@/pages/marketing/MarketingPage.vue'),
        meta: { breadcrumb: 'Marketing', permission: 'marketing:manage' },
      },
      {
        path: 'releases',
        name: 'releases',
        component: () => import('@/pages/ReleasesPage.vue'),
        meta: { breadcrumb: 'Novedades y estado' },
      },
      {
        path: 'app/operations',
        name: 'app-operations',
        component: () => import('@/pages/app/AppOperationsPage.vue'),
        meta: { breadcrumb: 'Operación en vivo', permission: 'app:view' },
      },
      {
        path: 'team',
        name: 'team',
        component: () => import('@/pages/TeamPage.vue'),
        meta: { breadcrumb: 'Equipo y ausencias' },
      },
      {
        path: 'leadership',
        name: 'leadership',
        component: () => import('@/pages/admin/LeadershipPage.vue'),
        meta: { breadcrumb: 'Liderazgo', permission: 'leadership:manage' },
      },
      {
        path: 'dev/monitor',
        name: 'dev-monitor',
        component: () => import('@/pages/dev/MonitorPage.vue'),
        meta: { breadcrumb: 'Monitor', permission: 'monitor:view' },
      },
      {
        path: 'ar',
        name: 'augmented-reality',
        component: () => import('@/pages/ar/AugmentedRealityPage.vue'),
        meta: { breadcrumb: 'Realidad aumentada', permission: 'ar:view' },
      },
      {
        path: 'infra',
        name: 'infra',
        component: () => import('@/pages/dev/InfraPage.vue'),
        meta: { breadcrumb: 'Servidor y nube', permission: 'infra:view' },
      },
      {
        path: 'dev/monitor/errors/:id',
        name: 'dev-monitor-error',
        component: () => import('@/pages/dev/ErrorDetailPage.vue'),
        meta: { breadcrumb: 'Detalle del error', permission: 'monitor:view' },
      },
      {
        path: 'admin/security',
        name: 'admin-security',
        component: () => import('@/pages/admin/SecurityPage.vue'),
        meta: { breadcrumb: 'Seguridad', permission: 'security:view' },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/pages/admin/UsersPage.vue'),
        meta: { breadcrumb: 'Usuarios', permission: 'user:manage' },
      },
      {
        path: 'admin/audit',
        name: 'admin-audit',
        component: () => import('@/pages/admin/AuditPage.vue'),
        meta: { breadcrumb: 'Auditoría', permission: 'audit:view' },
      },
      {
        path: 'admin/catalog',
        name: 'admin-catalog',
        component: () => import('@/pages/admin/CatalogPage.vue'),
        meta: { breadcrumb: 'Catálogo de casos', permission: 'catalog:manage' },
      },
      {
        path: 'admin/backups',
        name: 'admin-backups',
        component: () => import('@/pages/admin/BackupsPage.vue'),
        meta: { breadcrumb: 'Copias de seguridad', permission: 'infra:view' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { public: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!auth.isReady) {
    await auth.tryRestoreSession();
  }

  if (to.meta.public) {
    return true;
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const requiredPermission = to.meta.permission as string | undefined;
  const anyPermission = to.meta.anyPermission as string[] | undefined;
  if (
    (requiredPermission && !auth.hasPermission(requiredPermission)) ||
    (anyPermission && !anyPermission.some((p) => auth.hasPermission(p)))
  ) {
    return { name: 'forbidden', query: { from: to.fullPath } };
  }

  return true;
});
